/**
 * RecorderForm — ficha de un grabador NVR/DVR al estándar de la aplicación.
 *
 * Sustituye al modal: pantalla completa con topbar (volver, miga, estado,
 * Editar/Guardar/Descartar/Eliminar), pestañas **General**, **Ubicación**
 * (planta/sala + rack y U), **Cámaras** (las que cuelgan de él), **Contactos**
 * y el botón de Docs en la cabecera de tarjetas.
 *
 * El montaje en rack se guarda con `rack_id` + `position_u`; el backend lo
 * refleja en el visor como elemento `nvr` y valida el solape de U.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  ServerStackIcon,
  VideoCameraIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import ContactsPanel from './ContactsPanel';
import DocSmartButton from './DocSmartButton';
import ElementIpPanel from './ElementIpPanel';
import Dialog from './common/Dialog';
import { getServerFormStyle } from '../utils/serverFormStyle';

const FORM_VACIO = {
  id: null,
  name: '',
  code: '',
  parent_id: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  firmware_version: '',
  ip_address: '',
  mac_address: '',
  management_url: '',
  channels: '',
  storage_tb: '',
  retention_days: '',
  location_id: '',
  rack_id: '',
  position_u: '',
  size_u: 1,
  location_floor: '',
  location_room: '',
  status: 'active',
  notes: '',
};

const ESTADOS = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'decommissioned', label: 'Dado de baja' },
];

const TONO_ESTADO = {
  active: 'list-badge--on',
  maintenance: 'list-badge--warn',
  decommissioned: 'list-badge--muted',
  inactive: 'list-badge--off',
};

export default function RecorderForm({
  recorder,
  companyId,
  recorders = [],
  cameras = [],
  locations = [],
  racks = [],
  initialMode = 'view',
  onClose,
  onSaved,
  onDeleted,
  navItems = [],
  navIndex = -1,
  onNavigate,
}) {
  const isCards = getServerFormStyle() === 'cards';
  const [editing, setEditing] = useState(initialMode !== 'view' || !recorder?.id);
  const [form, setForm] = useState(() => ({ ...FORM_VACIO, ...(recorder || {}) }));
  const [base, setBase] = useState(() => ({ ...FORM_VACIO, ...(recorder || {}) }));
  const [tab, setTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const siguiente = { ...FORM_VACIO, ...(recorder || {}) };
    setForm(siguiente);
    setBase(siguiente);
    setEditing(initialMode !== 'view' || !recorder?.id);
  }, [recorder, initialMode]);

  const sf = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const dirty = useMemo(
    () => Object.keys({ ...FORM_VACIO, ...form }).some((key) => String(form[key] ?? '') !== String(base[key] ?? '')),
    [form, base],
  );

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty,
    saving,
    entity: 'este grabador',
    actionLabel: 'pasas a otro grabador o cierras la ficha',
    save: () => guardar({ keepEditing: editing }),
  });

  const statusLabel = ESTADOS.find((e) => e.value === form.status)?.label || form.status;
  const locationName = locations.find((l) => String(l.id) === String(form.location_id))?.name
    || form.location_name || '';
  const rackName = racks.find((r) => String(r.id) === String(form.rack_id))?.name
    || form.rack_name || '';
  const parentName = recorders.find((r) => String(r.id) === String(form.parent_id))?.name
    || form.parent_name || '';
  const camaras = useMemo(
    () => cameras.filter((c) => String(c.recorder_id) === String(form.id)),
    [cameras, form.id],
  );

  const guardar = useCallback(async ({ keepEditing = false } = {}) => {
    if (!form.name.trim()) {
      setError('El nombre es obligatorio');
      setTab('general');
      return false;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      ['id', 'created_at', 'updated_at', 'company_id', 'parent_name', 'location_name',
        'rack_name', 'rack_element_id', 'camera_count', 'status_label'].forEach((key) => delete payload[key]);
      payload.parent_id = form.parent_id || null;
      payload.location_id = form.location_id || null;
      payload.rack_id = form.rack_id || null;

      const guardado = form.id
        ? await api.updateRecorder(form.id, payload, companyId)
        : await api.createRecorder(payload, companyId);
      setForm({ ...FORM_VACIO, ...guardado });
      setBase({ ...FORM_VACIO, ...guardado });
      if (!keepEditing) setEditing(false);
      onSaved?.(guardado);
      return true;
    } catch (err) {
      setError(err.message || 'No se pudo guardar el grabador');
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, companyId, onSaved]);

  const descartar = () => {
    if (!form.id) { onClose?.(); return; }
    setForm({ ...base });
    setEditing(false);
    setError('');
  };

  const eliminar = async () => {
    if (!form.id) return;
    try {
      await api.deleteRecorder(form.id, companyId);
      setConfirmDelete(false);
      onDeleted?.(form);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el grabador');
      setConfirmDelete(false);
    }
  };

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'red', label: 'Red' },
    { key: 'ubicacion', label: 'Ubicación' },
    { key: 'camaras', label: `Cámaras${camaras.length ? ` (${camaras.length})` : ''}` },
    ...(form.id ? [{ key: 'contactos', label: 'Contactos' }] : []),
  ];

  /** Campo del estándar de tarjetas: `<div class="sf2-field">` con su etiqueta. */
  const campo = (label, key, placeholder, extraProps = {}) => (
    <div className="sf2-field">
      <label>{label}</label>
      {editing
        ? (
          <input
            value={form[key] ?? ''}
            onChange={(e) => sf(key, e.target.value)}
            placeholder={placeholder}
            {...extraProps}
          />
        )
        : <div className="v">{form[key] || '—'}</div>}
    </div>
  );

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} />
          Grabadores
        </button>
        <DetailCrumb label={form.name || 'Nuevo grabador'} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Grabador"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => onNavigate?.(destino))}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 320 }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          <span className={`list-badge ${TONO_ESTADO[form.status] || 'list-badge--muted'}`}>{statusLabel}</span>
          {!editing ? (
            <>
              {form.id && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)}>
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
            </>
          ) : (
            <>
              {form.id && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setConfirmDelete(true)}>
                  <TrashIcon style={{ width: 14, height: 14 }} /> Eliminar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={descartar} disabled={saving}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
              </button>
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={guardar}
                disabled={saving || !dirty}
                title={dirty ? 'Guardar los cambios' : 'Sin cambios'}
              >
                {saving ? <span className="spinner-border spinner-border-sm" /> : <CheckIcon style={{ width: 14, height: 14 }} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="dv-body">
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={(e) => e.preventDefault()}>
          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<ServerStackIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(value) => sf('name', value)}
                placeholder="Nombre del grabador…"
                editing={editing}
                saving={saving}
                onSave={guardar}
                onCancel={descartar}
                onEdit={() => setEditing(true)}
                crumb={[locationName, rackName].filter(Boolean).join(' › ')}
                badges={[{ text: statusLabel, color: form.status === 'active' ? '#16a34a' : '#64748b' }]}
                chips={[
                  { value: camaras.length, label: 'cámaras' },
                  ...(form.ip_address ? [{ value: form.ip_address, label: 'IP' }] : []),
                ]}
                dirty={dirty}
                inline
                titleDirty={dirty}
                onTitleCommit={() => { if (form.id) guardar(); }}
                onTitleReset={() => setForm({ ...base })}
                extra={form.id ? (
                  <DocSmartButton
                    elementType="nvr"
                    elementId={form.id}
                    elementName={form.name}
                    companyId={companyId}
                  />
                ) : null}
              />
            ) : (
              <div className="dv-record-header">
                <span className="dv-record-icon"><ServerStackIcon style={{ width: 18, height: 18 }} /></span>
                <input
                  className="dv-record-title"
                  value={form.name}
                  onChange={(e) => sf('name', e.target.value)}
                  placeholder="Nombre del grabador…"
                  disabled={!editing}
                />
              </div>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabs.map((t) => (
                  <TabButton
                    key={t.key}
                    type="button"
                    className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
            <fieldset className="dv-fieldset" disabled={!editing}>
              {tab === 'general' && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="sf2-grid sf2-grid--2">
                    <div className="sf2-card">
                      <h3>Identificación</h3>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        {campo('Código', 'code', 'NVR-0001')}
                        {campo('Fabricante', 'manufacturer', 'Hikvision, Dahua, Milestone…')}
                        {campo('Modelo', 'model', 'DS-7616NI-K2')}
                        {campo('Nº de serie', 'serial_number', 'S/N')}
                        {campo('Firmware', 'firmware_version', 'V4.30')}
                        <div className="sf2-field">
                          <label>Estado</label>
                          {editing ? (
                            <select value={form.status} onChange={(e) => sf('status', e.target.value)}>
                              {ESTADOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                          ) : <div className="v">{statusLabel}</div>}
                        </div>
                        <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                          <label>Grabador padre</label>
                          {editing ? (
                            <select value={form.parent_id || ''} onChange={(e) => sf('parent_id', e.target.value)}>
                              <option value="">— ninguno —</option>
                              {recorders
                                .filter((r) => String(r.id) !== String(form.id))
                                .map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                          ) : <div className="v">{parentName || '—'}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="sf2-card">
                      <h3>Notas</h3>
                      <div className="sf2-fields" style={{ '--n': 1 }}>
                        <div className="sf2-field">
                          <label>Observaciones</label>
                          {editing
                            ? (
                              <textarea
                                rows={8}
                                value={form.notes || ''}
                                onChange={(e) => sf('notes', e.target.value)}
                                placeholder="Ubicación del grabador, cámaras que cubre, incidencias…"
                              />
                            )
                            : <div className="v" style={{ whiteSpace: 'pre-wrap' }}>{form.notes || '—'}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === 'red' && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="sf2-grid sf2-grid--2">
                    <div className="sf2-card">
                      <h3>Conectividad</h3>
                      <div className="sf2-fields" style={{ '--n': 1 }}>
                        {campo('MAC', 'mac_address', 'AA:BB:CC:DD:EE:FF')}
                        <div className="sf2-field">
                          <label>IP de gestión (IP principal – se sincroniza con IPAM)</label>
                          {editing
                            ? (
                              <input
                                value={form.ip_address || ''}
                                onChange={(e) => sf('ip_address', e.target.value)}
                                placeholder="10.0.0.20"
                              />
                            )
                            : <div className="v">{form.ip_address || '—'}</div>}
                        </div>
                        <div className="sf2-field">
                          <label>URL de gestión</label>
                          {editing
                            ? <input value={form.management_url || ''} onChange={(e) => sf('management_url', e.target.value)} placeholder="http://10.0.0.20" />
                            : (
                              <div className="v">
                                {form.management_url
                                  ? (
                                    <a href={form.management_url} target="_blank" rel="noreferrer">
                                      {form.management_url} <ArrowTopRightOnSquareIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} />
                                    </a>
                                  ) : '—'}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="sf2-card">
                      <h3>Capacidad</h3>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        {campo('Canales', 'channels', '16', { type: 'number' })}
                        {campo('Almacenamiento (TB)', 'storage_tb', '8', { type: 'number', step: '0.1' })}
                        {campo('Retención (días)', 'retention_days', '30', { type: 'number' })}
                      </div>
                    </div>
                  </div>

                  {/* Mismo bloque que estación de trabajo, impresora o servidor:
                      las IPs del equipo se ven y se tocan aquí, contra el IPAM.
                      En alta sale deshabilitado hasta guardar el grabador. */}
                  <div className="dv-section" style={{ marginTop: 16 }}>
                    <div className="dv-section-title">IPAM — Asignaciones de IP</div>
                    <ElementIpPanel
                      objectType="nvr"
                      objectId={form.id}
                      companyId={companyId}
                      elementName={form.name}
                    />
                  </div>
                </div>
              )}

              {tab === 'ubicacion' && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="sf2-card">
                    <h3>Ubicación y rack</h3>
                    <div className="sf2-fields" style={{ '--n': 2 }}>
                      <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                        <label>Ubicación / centro</label>
                        {editing ? (
                          <select value={form.location_id || ''} onChange={(e) => sf('location_id', e.target.value)}>
                            <option value="">— sin ubicación —</option>
                            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                          </select>
                        ) : <div className="v">{locationName || '—'}</div>}
                      </div>
                      {campo('Planta', 'location_floor', 'Planta 0')}
                      {campo('Sala', 'location_room', 'Cuarto de comunicaciones')}
                      <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                        <label>Rack</label>
                        {editing ? (
                          <select value={form.rack_id || ''} onChange={(e) => sf('rack_id', e.target.value)}>
                            <option value="">— sin rack —</option>
                            {racks.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}{r.location_name ? ` · ${r.location_name}` : ''}
                              </option>
                            ))}
                          </select>
                        ) : <div className="v">{rackName || '—'}</div>}
                      </div>
                      {campo('U inicial', 'position_u', '10', { type: 'number' })}
                      {campo('Tamaño (U)', 'size_u', '1', { type: 'number' })}
                    </div>
                    <p className="sf2-hint" style={{ marginTop: 10 }}>
                      Al indicar rack y U, el grabador aparece montado en el visor del rack (y se
                      comprueba que la U esté libre).
                    </p>
                  </div>
                </div>
              )}

              {tab === 'camaras' && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="dv-section">
                    <div className="dv-section-title">
                      Cámaras de este grabador ({camaras.length})
                    </div>
                    {camaras.length === 0 && (
                      <p className="list-hint" style={{ margin: 0 }}>
                        Sin cámaras asignadas. Se asignan desde la ficha de cada cámara (campo «Grabador»).
                      </p>
                    )}
                    {camaras.map((camera) => (
                      <div
                        key={camera.id}
                        style={{
                          display: 'flex', gap: 10, alignItems: 'center',
                          padding: '7px 0', borderTop: '1px solid var(--border)', flexWrap: 'wrap',
                        }}
                      >
                        <VideoCameraIcon style={{ width: 15, height: 15, color: 'var(--accent-l)' }} />
                        <strong style={{ fontSize: '0.86rem' }}>{camera.name}</strong>
                        {camera.channel ? <span className="list-badge">canal {camera.channel}</span> : null}
                        {camera.resolution ? <span className="list-badge list-badge--muted">{camera.resolution}</span> : null}
                        {camera.ip_address && <span className="list-code" style={{ marginLeft: 'auto' }}>{camera.ip_address}</span>}
                        <span className={`list-badge ${camera.status === 'active' ? 'list-badge--on' : 'list-badge--off'}`}>
                          {camera.status_label || camera.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === 'contactos' && form.id && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="dv-section">
                    <div className="dv-section-title">Contactos</div>
                    <ContactsPanel
                      companyId={companyId}
                      objectType="nvr"
                      objectId={form.id}
                      objectName={form.name}
                    />
                  </div>
                </div>
              )}
            </fieldset>
            <FormDirtyDock
              show={dirty}
              saving={saving}
              onDiscard={descartar}
              onSave={() => guardar()}
            />
          </FormCardsShell>
        </form>
      </div>

      {confirmDelete && (
        <Dialog
          title="Eliminar grabador"
          onClose={() => setConfirmDelete(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmDelete(false)}>Cancelar</button>
              <button type="button" className="list-btn-danger" onClick={eliminar}>Eliminar</button>
            </>
          )}
        >
          <p>
            ¿Eliminar el grabador «{form.name}»? Sus {camaras.length} cámara(s) se quedan sin
            grabador y también se desmonta del rack.
          </p>
        </Dialog>
      )}
    </div>
  );
}
